/* Sphere orb shaders — extracted VERBATIM from Desktop/sphere-orb.html
   (the confirmed-working orb). Do not hand-edit. */
export const SPHERE_VERT = `attribute vec2 p; varying vec2 v_uv;
void main(){ v_uv=(p+1.0)*0.5; gl_Position=vec4(p,0.0,1.0); }`;

export const SPHERE_FRAG = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 v_uv;
uniform vec2  u_res;
uniform float u_time;        // animationTime * 0.42
uniform float u_yaw;
uniform vec2  u_lightDir;
uniform vec2  u_tapUV;
uniform float u_tapTime;
uniform float u_flowPhase;
uniform float u_ambient;
uniform float u_beat;
uniform float u_loading;
uniform float u_radius;
uniform float u_saturation;
uniform float u_contrast;
uniform float u_specularI;
uniform float u_rimI;
uniform float u_grayscale;
uniform float u_redness;
uniform float u_green;
uniform vec3  u_tint;      // per-state hue the marble leans into
uniform float u_tintAmt;   // 0 = untouched marble, 1 = full lean
uniform float u_zoom;          // 0 = sphere, 1 = flat fullscreen (animated dive-in)
uniform float u_fieldPerPixel; // flat view: field units per device pixel, matched to the sphere
uniform vec3  u_base0, u_base1, u_base2;  // navy, recovery, sleep
uniform vec3  u_met0, u_met1, u_met2;     // teal-hold, terracotta, warning red

float sphereHash(vec3 p){
  return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453123);
}

float sphereNoise(vec3 p){
  vec3 i=floor(p);
  vec3 f=fract(p);
  vec3 u=f*f*(3.0-2.0*f);
  float n000=sphereHash(i+vec3(0.0,0.0,0.0));
  float n100=sphereHash(i+vec3(1.0,0.0,0.0));
  float n010=sphereHash(i+vec3(0.0,1.0,0.0));
  float n110=sphereHash(i+vec3(1.0,1.0,0.0));
  float n001=sphereHash(i+vec3(0.0,0.0,1.0));
  float n101=sphereHash(i+vec3(1.0,0.0,1.0));
  float n011=sphereHash(i+vec3(0.0,1.0,1.0));
  float n111=sphereHash(i+vec3(1.0,1.0,1.0));
  float nx00=mix(n000,n100,u.x);
  float nx10=mix(n010,n110,u.x);
  float nx01=mix(n001,n101,u.x);
  float nx11=mix(n011,n111,u.x);
  float nxy0=mix(nx00,nx10,u.y);
  float nxy1=mix(nx01,nx11,u.y);
  return mix(nxy0,nxy1,u.z);
}

// 6 octaves with the same double axis-rotation between octaves as the Metal source
float sphereFbm(vec3 p){
  float v=0.0;
  float a=0.4;
  for(int i=0;i<6;i++){
    v+=a*sphereNoise(p);
    p=vec3(0.8*p.x-0.6*p.y, 0.6*p.x+0.8*p.y, p.z);
    p=vec3(p.x, 0.8*p.y-0.6*p.z, 0.6*p.y+0.8*p.z);
    p*=2.02;
    a*=0.5;
  }
  return v;
}

vec3 rotateY(vec3 v, float angle){
  float c=cos(angle);
  float s=sin(angle);
  return vec3(c*v.x+s*v.z, v.y, -s*v.x+c*v.z);
}

// FLOWING liquid-marble ramp: navy dominates low band, recovery mid, sleep teal
// held twice, terracotta only above ~0.82 (cream stop deliberately removed)
vec3 ramp5(float t, vec3 c0, vec3 c1, vec3 c2, vec3 c3, vec3 c4){
  t=clamp(t,0.0,1.0);
  if(t<0.34) return mix(c0,c1,t/0.34);
  if(t<0.54) return mix(c1,c2,(t-0.34)/0.20);
  if(t<0.64) return mix(c2,c3,(t-0.54)/0.10);
  return mix(c3,c4,(t-0.64)/0.36);
}

vec3 sphereSatCon(vec3 c, float sat, float con){
  float l=dot(c, vec3(0.2126,0.7152,0.0722));
  c=mix(vec3(l),c,sat);
  c=(c-0.5)*con+0.5;
  return c;
}

void main(){
  float aspect=u_res.x/max(u_res.y,1.0);
  vec2 centered=(v_uv-0.5)*2.0;
  centered.x*=aspect;

  float radial=length(centered);
  float aa=max(2.0/u_res.y,0.002)*1.5;
  float ez=u_zoom*u_zoom*(3.0-2.0*u_zoom);   // eased dive progress (0 sphere -> 1 fullscreen)

  // DIVE = a magnification into the sphere's CENTRE. mag grows, so we sample an
  // ever-smaller central patch blown up to fill the screen: the SAME marble, just
  // zoomed in (no pattern morph, no 'new shader'). A sphere's centre is locally
  // flat and the curved rim simply flies off-screen, so nothing un-morphs on view.
  float mag=mix(1.0, 1.8, ez);
  vec2 c=centered/mag;

  // resting sphere mode stays cheap — skip pixels outside the ball
  if(u_zoom<0.001 && dot(c,c)>(u_radius+aa)*(u_radius+aa)){ gl_FragColor=vec4(0.0); return; }

  float inside=step(dot(c,c), u_radius*u_radius);
  float zc=sqrt(max(0.0,u_radius*u_radius-dot(c,c)));
  vec3 N=normalize(vec3(c,zc)+vec3(0.0,0.0,1e-5));

  // curved sphere inside the ball; flat continuation of the SAME field outside so
  // the screen fills seamlessly. The inside also relaxes to flat as we zoom, but
  // only the off-screen-bound periphery is affected — the magnified centre is
  // already flat (N ~= flatDir there), so the visible texture never morphs.
  vec3 flatDir=vec3(c/max(u_radius,1e-4), 1.0);
  vec3 curved=mix(flatDir, N, inside);
  vec3 dir=mix(curved, flatDir, smoothstep(0.0,0.35,ez)); // relax curvature early -> seamless
  vec3 fieldPos=rotateY(dir,u_yaw)*0.50;

  float er=clamp(length(c)/max(u_radius,1e-4),0.0,1.5); // 0 centre -> 1 limb
  vec3 V=vec3(0.0,0.0,1.0);
  float ft=u_flowPhase;

  vec3 q=vec3(
    sphereFbm(fieldPos*0.9+vec3(0.0,ft,0.0)),
    sphereFbm(fieldPos*0.9+vec3(5.2,-ft*0.8,2.0)),
    sphereFbm(fieldPos*0.9+vec3(2.7,ft*0.6,7.0))
  );
  vec3 rr=vec3(
    sphereFbm(fieldPos*1.1+2.0*q+vec3(1.7,9.2,0.0)+ft*0.5),
    sphereFbm(fieldPos*1.1+2.0*q+vec3(8.3,2.8,4.0)-ft*0.4),
    sphereFbm(fieldPos*1.1+2.0*q+vec3(3.1,5.5,9.0)+ft*0.3)
  );
  vec3 field=fieldPos*1.2+2.4*rr;
  float rawHeight=sphereFbm(field);

  // contrast-expand the height histogram around its ~0.40 mean
  float height=clamp((rawHeight-0.40)*2.6+0.44,0.0,1.0);
  float rrx=clamp((rr.x-0.40)*2.0+0.42,0.0,1.0);

  // ---- band flows with the marble -> colours roil = motion
  float band=smoothstep(0.12,0.95,height*0.95+0.30*rrx);
  vec3 col=ramp5(band,u_base0,u_base1,u_base2,u_met0,u_met1)*u_beat;

  // ---- per-state accent bleed: grow continents of ONE of our own signal
  // colours (recovery blue / sleep teal / movement orange) — same mechanic
  // as the green/red bleed, so the marble's own colours concentrate per state
  if(u_tintAmt>0.0){
    float amt=clamp(u_tintAmt*2.5,0.0,1.0);
    float accMask=smoothstep(1.0-amt*1.6,1.0-amt*1.1,band);
    vec3 accCol=u_tint*(0.45+1.05*band);
    col=mix(col,accCol,accMask);
  }

  // ---- additive micro-sparkle on the crests (#E6EBFF)
  float msp=pow(smoothstep(0.62,1.0,height+0.2*rr.y),4.5);
  col+=msp*vec3(0.9,0.92,1.0)*0.20;

  // ---- low-score red warning: flowing continents from the hot end of the field
  if(u_redness>0.0){
    float amt=clamp(u_redness*2.5,0.0,1.0);
    float redMask=smoothstep(1.0-amt*1.6,1.0-amt*1.1,band);
    vec3 redCol=u_met2*(0.45+1.05*band);
    col=mix(col,redCol,redMask);
  }

  // ---- peak-recovery green: in-range continents bleed in from the field
  if(u_green>0.0){
    float amt=clamp(u_green*2.5,0.0,1.0);
    float greenMask=smoothstep(1.0-amt*1.6,1.0-amt*1.1,band);
    vec3 greenCol=vec3(0.310,0.812,0.431)*(0.45+1.05*band);
    col=mix(col,greenCol,greenMask);
  }

  // ---- pill V11 lighting: sphere shading fades out FAST (before the circle edge
  // sweeps outward) so no lit rim/limb ring is ever visible at the boundary ----
  float sphereLight=(1.0-smoothstep(0.0,0.30,ez))*inside;
  vec3 Lw=normalize(vec3(u_lightDir,0.6));
  float ndl=dot(N,Lw);
  float flatDiffuse=mix(u_ambient,1.14,smoothstep(-0.62,1.0,Lw.z));
  float sphereDiffuse=mix(u_ambient,1.14,smoothstep(-0.62,1.0,ndl));
  col*=mix(flatDiffuse,sphereDiffuse,sphereLight);
  vec3 Hn=normalize(Lw+V);

  // relief specular following the liquid surface (surface property; both views)
  float e=0.02;
  float hx=sphereFbm(field+vec3(e,0.0,0.0))-rawHeight;
  float hy=sphereFbm(field+vec3(0.0,e,0.0))-rawHeight;
  vec3 reliefN=normalize(vec3(hx*7.0,hy*7.0,1.0));
  float specL=pow(clamp(dot(reliefN,Hn),0.0,1.0),40.0);
  col+=specL*u_specularI*vec3(1.0,0.98,0.95);

  // crescent rim + inner shade + limb darkening — sphere shell only, fade on dive
  vec2 ndir=normalize(centered+vec2(1e-5));
  vec2 Ldir=normalize(u_lightDir);
  float sideDot=dot(ndir,Ldir);
  float litMask=pow(clamp(sideDot,0.0,1.0),2.6);
  float rim=smoothstep(0.55,0.95,er)*litMask;
  col+=rim*u_rimI*vec3(1.0,0.99,0.96)*sphereLight;

  float shadowSide=pow(clamp(-sideDot,0.0,1.0),1.4);
  float innerShade=smoothstep(0.15,0.95,er)*shadowSide;
  col*=mix(1.0,0.70,innerShade*sphereLight);

  float vignette=smoothstep(1.06,0.40,er);
  col*=mix(1.0,mix(0.80,1.06,vignette),sphereLight);

  // ---- ONE soft-edged circle that expands to cover the whole screen by the first
  // HALF of the dive (so the boundary is gone well before the end — the rest is a
  // pure zoom into fullscreen marble). Lighting has already faded and the marble is
  // seamless, so the circle simply 'becomes' the screen — no ring forms around it. ----
  float coverR=sqrt(aspect*aspect+1.0)+0.25;         // reaches every corner
  float edgeR=mix(u_radius, coverR, smoothstep(0.0,0.5,ez));
  float feather=mix(aa, 0.16, smoothstep(0.0,0.18,ez)); // crisp sphere at rest, soft while growing
  float alpha=1.0-smoothstep(edgeR-feather, edgeR+feather, radial);

  // final grade (gentle; stops are pre-calibrated)
  col=sphereSatCon(col,u_saturation,u_contrast);

  // ---- tap ripple (NaN-safe: dd*dd, never pow(negative))
  float sinceTap=u_time-u_tapTime;
  if(sinceTap>0.0&&sinceTap<0.6){
    vec2 tapCentered=(u_tapUV-0.5)*2.0;
    tapCentered.x*=aspect;
    float dist=length(centered-tapCentered);
    float waveFront=sinceTap*2.2;
    float dd=(dist-waveFront)/0.28;
    float ring=exp(-dd*dd)*exp(-sinceTap*4.0);
    col+=ring*0.18*vec3(1.0);
  }

  // ---- loading: crossfade to neutral pulsing grey
  if(u_loading>0.0){
    float g=mix(0.30,0.62,height);
    float gp=0.86+0.14*sin(u_time*2.2);
    col=mix(col,vec3(g*gp),clamp(u_loading,0.0,1.0));
  }

  float luminance=dot(col,vec3(0.299,0.587,0.114));
  col=mix(col,vec3(luminance),u_grayscale);

  col=clamp(col,0.0,1.0);
  gl_FragColor=vec4(col*alpha,alpha); // premultiplied
}
`;
